import { getErrorMessage } from '@/lib/api/client';
import { toast } from '@/lib/utils/toast';
import { createComponentLogger } from '@/lib/utils/logger';

const logger = createComponentLogger('GlobalErrors');

const GENERIC_MESSAGE = 'Something unexpected happened. Please try again.';
/** Don't show more than one global toast within this window (a crash can fire in a burst). */
const THROTTLE_MS = 5000;

let installed = false;
let lastShownAt = 0;

/** Browser noise that is harmless and must never reach the user. */
function isIgnorable(reason: unknown, message?: string): boolean {
  const text = `${message ?? ''} ${reason instanceof Error ? reason.message : String(reason ?? '')}`;
  if (reason instanceof DOMException && reason.name === 'AbortError') return true;
  if (/ResizeObserver loop/i.test(text)) return true;
  if (/^\s*Script error\.?\s*$/i.test(text)) return true;
  return false;
}

function report(reason: unknown, message?: string): void {
  if (isIgnorable(reason, message)) return;

  logger.error('Unhandled error:', reason ?? message);

  const now = Date.now();
  if (now - lastShownAt < THROTTLE_MS) return;
  lastShownAt = now;

  const text = reason instanceof Error ? getErrorMessage(reason) : GENERIC_MESSAGE;
  // A stable id means the same message never stacks.
  toast.error(text, { id: `global-error:${text}` });
}

/**
 * Last line of defence for errors nothing else caught: promise rejections
 * without a handler and uncaught runtime errors. They are logged and surfaced
 * as one friendly toast instead of failing silently. Render errors are handled
 * by the ErrorBoundary. Safe to call more than once.
 */
export function installGlobalErrorHandlers(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('unhandledrejection', (event) => {
    report(event.reason);
  });

  window.addEventListener('error', (event) => {
    // Resource load failures (images, scripts) bubble here without an `error`.
    if (!event.error && !event.message) return;
    report(event.error, event.message);
  });
}
